import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { STUDIO } from "@/lib/siteData";

// Frames 16:3 → 16:73 → 16:114 are not three sections. They are the same canvas
// three times: empty orange, then the logotype, then the logotype plus five
// rotated keyword pills — with every element at byte-identical coordinates.
// That is an entrance sequence, so it is built as one animated hero.
//
// The composition is absolutely positioned on the design's 1440x800 canvas, so
// offsets are percentages of it and type scales with `cqw` against a container
// of the same aspect ratio. The arrangement then holds at any width.
const CANVAS = { width: 1440, height: 800 };

const pct = (value, axis) => `${(value / CANVAS[axis]) * 100}%`;

// Centre of each pill — the Figma boxes are the rotated bounding boxes, which
// are larger than the pill itself.
//
// `from` is where the badge flies in from, in viewport units so it always starts
// off-screen. Each one enters from the edge nearest its final position, so they
// converge on the logotype from five directions instead of arriving together.
// `spin` is the extra rotation it carries in, unwinding to the designed angle as
// it settles.
const KEYWORDS = [
    {
        label: "DESIGN",
        x: 411.36, y: 281.7, width: 207, rotate: -12.66,
        from: { x: "-70vw", y: "-14vh" }, spin: -22,
    },
    {
        label: "CRIATIVIDADE",
        x: 734.04, y: 285.43, width: 338, rotate: 6.69,
        from: { x: "0vw", y: "-75vh" }, spin: 16,
    },
    {
        label: "BRANDING",
        x: 1009.3, y: 383.85, width: 252, rotate: -8.35,
        from: { x: "70vw", y: "-8vh" }, spin: -18,
    },
    {
        label: "ESTRATÉGIA",
        x: 548.09, y: 452.6, width: 289, rotate: 6.26,
        from: { x: "-70vw", y: "18vh" }, spin: 20,
    },
    {
        label: "INOVAÇÃO",
        x: 880.96, y: 527.27, width: 250, rotate: -5.71,
        from: { x: "36vw", y: "70vh" }, spin: -14,
    },
];

const LOGO = { x: 302, y: 275, width: 709, height: 274 };
const PILL_HEIGHT = 72;

const EASE = [0.16, 1, 0.3, 1];

// The entrance replays every time the hero returns to view, so scrolling away and
// coming back to the top plays it again — Arthur's request.
//
// The mechanism, arrived at by measuring what actually worked:
//
// The trigger watches the SECTION, because the badges start at x: -70vw — off
// screen — so an in-view trigger on a badge itself can never fire.
//
// The replay remounts the animated elements via a changing key, which makes their
// initial→animate entrance run again from the start. Two other approaches were
// tried and measured as not working: whileInView with `once: false` never reverted
// (the badges still read opacity 1 with the hero 1600px clear of the viewport),
// and driving a variant from useInView played the entrance but not its reverse.
// Remounting is blunt but it is the same code path as the first play, so it cannot
// drift from it.
const IN_VIEW_OPTIONS = { amount: 0.35 };

// Counts how many times the hero has re-entered view. Used as a key suffix.
const useEntranceRuns = (isInView, enabled) => {
    const [runs, setRuns] = useState(0);
    const wasInView = useRef(true);

    useEffect(() => {
        if (!enabled) return;
        if (isInView && !wasInView.current) setRuns((current) => current + 1);
        wasInView.current = isInView;
    }, [isInView, enabled]);

    return runs;
};

export const Hero = () => {
    const reduceMotion = useReducedMotion();
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, IN_VIEW_OPTIONS);

    const runs = useEntranceRuns(isInView, !reduceMotion);

    // With reduced motion nothing animates: elements are simply present.
    const logoMotion = reduceMotion
        ? {}
        : {
              initial: { opacity: 0, scale: 0.96 },
              animate: { opacity: 1, scale: 1 },
              transition: { duration: 0.9, ease: EASE },
          };

    const pillMotion = (word, index) =>
        reduceMotion
            ? { style: { rotate: word.rotate } }
            : {
                  initial: {
                      x: word.from.x,
                      y: word.from.y,
                      rotate: word.rotate + word.spin,
                      opacity: 0,
                  },
                  animate: { x: 0, y: 0, rotate: word.rotate, opacity: 1 },
                  transition: {
                      duration: 1.15,
                      ease: EASE,
                      // They arrive one after another, once the logotype has settled.
                      delay: 0.55 + index * 0.14,
                      opacity: { duration: 0.3, delay: 0.55 + index * 0.14 },
                  },
              };

    const stackedPillMotion = (index) =>
        reduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 12 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.45, ease: EASE, delay: 0.3 + index * 0.08 },
              };

    return (
        // overflow-hidden matters: the badges start outside the viewport, and
        // without it they would widen the page and create horizontal scroll.
        <section
            ref={sectionRef}
            className="overflow-hidden bg-brand-fade"
            aria-label={STUDIO.name}
        >
            {/* Narrow viewports: the rotated pills overlap and the type would
                scale to ~10px, so the same content stacks instead. The Figma has
                no mobile frame — this arrangement is ours. */}
            <div className="flex flex-col items-center gap-10 px-6 py-20 md:hidden">
                <motion.img
                    src="/images/brand/logo-principal-branca.png"
                    alt={STUDIO.name}
                    width={1425}
                    height={551}
                    className="h-auto w-full max-w-[420px]"
                    key={`logo-stacked-${runs}`}
                    {...logoMotion}
                />
                <ul className="flex flex-wrap justify-center gap-3">
                    {KEYWORDS.map((word, index) => (
                        <motion.li
                            key={`${word.label}-${runs}`}
                            className="bg-brand-burnt px-5 py-2 text-[18px] font-bold italic text-paper"
                            {...stackedPillMotion(index)}
                        >
                            {word.label}
                        </motion.li>
                    ))}
                </ul>
            </div>

            <div
                className="relative mx-auto hidden w-full max-w-[1440px] md:block"
                style={{
                    containerType: "size",
                    aspectRatio: `${CANVAS.width} / ${CANVAS.height}`,
                }}
            >
                <motion.img
                    src="/images/brand/logo-principal-branca.png"
                    alt={STUDIO.name}
                    width={1425}
                    height={551}
                    className="absolute max-w-none object-contain"
                    style={{
                        left: pct(LOGO.x, "width"),
                        top: pct(LOGO.y, "height"),
                        width: pct(LOGO.width, "width"),
                        height: pct(LOGO.height, "height"),
                    }}
                    key={`logo-${runs}`}
                    {...logoMotion}
                />

                <ul>
                    {KEYWORDS.map((word, index) => (
                        // The <li> only positions: centring lives in a plain CSS
                        // transform so the animated x/y inside it stays free.
                        <li
                            key={word.label}
                            className="absolute"
                            style={{
                                left: pct(word.x, "width"),
                                top: pct(word.y, "height"),
                                width: pct(word.width, "width"),
                                height: pct(PILL_HEIGHT, "height"),
                                transform: "translate(-50%, -50%)",
                            }}
                        >
                            <motion.div
                                className="flex h-full w-full items-center justify-center bg-brand-burnt"
                                key={`${word.label}-${runs}`}
                                {...pillMotion(word, index)}
                            >
                                {/* 40px on the 1440 canvas. */}
                                <span
                                    className="whitespace-nowrap font-bold italic text-paper"
                                    style={{ fontSize: "2.778cqw" }}
                                >
                                    {word.label}
                                </span>
                            </motion.div>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};
